import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  Button, TextField, Checkbox,
  Snackbar, Alert,
  Dialog, DialogTitle, DialogContent,
  DialogActions
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import axios from 'axios';

interface FieldConfig {
  id?: number;
  field: string;
  headerName: string;
  editable: boolean;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const MathReportFields: React.FC = () => {
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingField, setEditingField] = useState<FieldConfig | null>(null);
  const [newField, setNewField] = useState<FieldConfig>({
    field: '',
    headerName: '',
    editable: false
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/mathreport-fields');
      const transformedFields = response.data.map((field: any) => ({
        ...field,
        editable: Boolean(field.editable)
      }));
      setFields(transformedFields);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при получении полей: ' + error,
        severity: 'error'
      });
    }
  };

  const handleOpenDialog = (field?: FieldConfig) => {
    if (field) {
      setIsEditing(true);
      setEditingField(field);
      setNewField({
        ...field,
        editable: Boolean(field.editable)
      });
    } else {
      setIsEditing(false);
      setEditingField(null);
      setNewField({ field: '', headerName: '', editable: false });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditing(false);
    setEditingField(null);
    setNewField({ field: '', headerName: '', editable: false });
  };

  const handleAddField = async () => {
    try {
      if (!newField.field || !newField.headerName) {
        setSnackbar({
          open: true,
          message: 'Заполните все обязательные поля',
          severity: 'error'
        });
        return;
      }

      if (!isEditing && fields.some(f => f.field === newField.field)) {
        setSnackbar({
          open: true,
          message: 'Поле с таким идентификатором уже существует',
          severity: 'error'
        });
        return;
      }

      if (isEditing && editingField) {
        await axios.put(`https://tbl.zerdegroup.kz/api/mathreport-fields/${editingField.field}`, newField);
        setSnackbar({
          open: true,
          message: 'Поле успешно обновлено',
          severity: 'success'
        });
      } else {
        await axios.post('https://tbl.zerdegroup.kz/api/mathreport-fields', newField);
        setSnackbar({
          open: true,
          message: 'Поле успешно добавлено',
          severity: 'success'
        });
      }

      handleCloseDialog();
      fetchFields();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Ошибка при ${isEditing ? 'обновлении' : 'добавлении'} поля: ` + error,
        severity: 'error'
      });
    }
  };

  const handleDeleteField = async (field: string) => {
    try {
      await axios.delete(`https://tbl.zerdegroup.kz/api/mathreport-fields/${field}`);
      setSnackbar({
        open: true,
        message: 'Поле успешно удалено',
        severity: 'success'
      });
      fetchFields();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при удалении поля: ' + error,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить поле
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Идентификатор поля</TableCell>
                <TableCell>Заголовок</TableCell>
                <TableCell>Редактируемое</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field) => (
                <TableRow key={field.field}>
                  <TableCell>{field.field}</TableCell>
                  <TableCell>{field.headerName}</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={Boolean(field.editable)} 
                      disabled
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex' }}>
                      <Button
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(field)}
                        sx={{ p: 0 }}
                      >
                      </Button>
                      <Button
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteField(field.field)}
                        sx={{ p: 0 }}
                      >
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>
          {isEditing ? 'Редактировать поле' : 'Добавить новое поле'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Идентификатор поля"
              value={newField.field}
              onChange={(e) => setNewField({ ...newField, field: e.target.value })}
              helperText="Используется в коде для обращения к полю"
              required
              disabled={isEditing}
            />
            <TextField
              label="Заголовок"
              value={newField.headerName}
              onChange={(e) => setNewField({ ...newField, headerName: e.target.value })}
              helperText="Отображается в таблице"
              required
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={newField.editable}
                onChange={(e) => setNewField({ ...newField, editable: e.target.checked })}
              />
              <span>Редактируемое поле</span>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleAddField} variant="contained">
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MathReportFields;